import { appThemeApiRef, useApi } from '@backstage/frontend-plugin-api';
import { Button } from '@vivicta/ui';
import { Moon, Sun } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Light/dark theme toggle. Defaults to following the resolved app theme
 * (explicit user choice, falling back to the OS `prefers-color-scheme`
 * setting) by observing the `.dark` class that the theme module keeps in
 * sync on `<html>`, then persists an explicit choice via the app's
 * `AppThemeApi` once clicked.
 */
export const ThemeToggleButton = () => {
  const appThemeApi = useApi(appThemeApiRef);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      setIsDark(root.classList.contains('dark'));
    });
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    appThemeApi.setActiveThemeId(isDark ? 'vivicta-light' : 'vivicta-dark');
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light theme' : 'Switch to dark theme'}
    >
      {isDark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </Button>
  );
};
